import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useI18n } from "@/lib/i18n";
import { PageShell } from "@/components/site/PageShell";
import { storiesListQuery, categoriesAllQuery, formatLogDate, type StoryListItem } from "@/lib/stories";
import type { Lang } from "@/lib/i18n";
import { CATEGORIES } from "@/lib/categories";

export const Route = createFileRoute("/$lang/stories/")({
  head: ({ params }) => ({
    meta: [
      { title: "The archive — MadCaptains" },
      { name: "description", content: "Pre-moderated, anonymous testimony from the merchant fleet." },
      { property: "og:title", content: "The archive — MadCaptains" },
      { property: "og:description", content: "Pre-moderated, anonymous testimony from the merchant fleet." },
      { property: "og:url", content: `/${params.lang}/stories` },
    ],
    links: [{ rel: "canonical", href: `/${params.lang}/stories` }],
  }),
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(storiesListQuery(params.lang as Lang)),
      context.queryClient.ensureQueryData(categoriesAllQuery()),
    ]),
  component: StoriesIndex,
  errorComponent: StoriesError,
  notFoundComponent: () => null,
});

function StoriesIndex() {
  const { lang, t } = useI18n();
  const { data: stories } = useSuspenseQuery(storiesListQuery(lang));
  const { data: cats } = useSuspenseQuery(categoriesAllQuery());
  const idToSlug = new Map(cats.map((c) => [c.id, c.slug]));

  if (stories.length === 0) {
    return (
      <PageShell eyebrow="Logbook" title={t.page_stories_h1} lead={t.page_stories_lead}>
        <div className="rounded-sm border border-dashed border-border bg-card/30 p-8 text-center">
          <p className="font-display text-lg italic text-muted-foreground">{t.page_stories_empty}</p>
          <Link
            to="/$lang/submit"
            params={{ lang }}
            className="mt-5 inline-flex rounded-full px-4 py-2 text-sm font-medium"
            style={{ background: "var(--amber)", color: "var(--ink)" }}
          >
            {t.nav_submit_cta}
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell eyebrow="Logbook" title={t.page_stories_h1} lead={t.page_stories_lead}>
      <ol className="divide-y divide-border border-y border-border">
        {stories.map((s, i) => (
          <StoryRow key={s.id} story={s} index={i} lang={lang} idToSlug={idToSlug} />
        ))}
      </ol>
    </PageShell>
  );
}

function StoryRow({
  story,
  index,
  lang,
  idToSlug,
}: {
  story: StoryListItem;
  index: number;
  lang: Lang;
  idToSlug: Map<string, string>;
}) {
  
  const flags = (story.category_ids ?? [])
    .map((id) => CATEGORIES.find((c) => c.slug === idToSlug.get(id))?.flag)
    .filter(Boolean) as string[];
  return (
    <li>
      <a
        href={`/${lang}/stories/${story.slug}`}
        className="group flex items-baseline gap-4 px-1 py-5 hover:bg-card/50"
      >
        <span className="w-12 text-right font-display text-xs text-muted-foreground tabular-nums">
          {String(index + 1).padStart(3, "0")}
        </span>
        <span className="flex-1">
          <span className="block font-display text-lg leading-snug">
            {story.title ?? "Untitled entry"}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{formatLogDate(story.published_at, lang)}</span>
            {story.author_display && <span>· {story.author_display}</span>}
            {flags.length > 0 && <span>· {flags.join(" ").toUpperCase()}</span>}
          </span>
        </span>
        <span className="text-xs text-muted-foreground group-hover:text-foreground">→</span>
      </a>
    </li>
  );
}

function StoriesError({ error }: { error: Error }) {
  return (
    <PageShell eyebrow="Logbook" title="The archive">
      <p className="text-sm text-muted-foreground">
        Could not load the archive: {error.message}
      </p>
    </PageShell>
  );
}